// The Fleet breakdown: a block's sessions as one whole split into its parts, each
// part opening to its own. Agents splits by the four kinds of agent, then
// agent and model; models splits by where inference ran (on the endpoint, or
// hosted by a provider), then model and agent; local inference splits the sessions whose model ran on the
// endpoint by model, then agent, with the runtimes seen running beside them.
// Sizes are sessions and nothing else. At most TOP parts are named at a level,
// each at least MIN_SHARE of it; the rest are one part that opens to them.
import { ENDPOINT_SOURCES, captureName, installedKey, isRuntimeProduct, modelKey, resolveAgent, runtimeOf } from "./fleetConsole.js";
import { deriveAgentIdentity, humanizeAgentName } from "./agentIdentity.js";
import { quoteValueIfNeeded as quote } from "./queryLang.js";

export const TOP = 8;
export const MIN_SHARE = 0.02;

// In the order of how far each is from the endpoint.
export const KINDS = { remote: "Remote", web: "Web chat", hybrid: "Hybrid", local: "Local" };
export const PLACES = { remote: "Remote inference", local: "Local inference" };
const FIXED = { kind: KINDS, place: PLACES };
const PLURAL = { agent: "agents", model: "models" };
export const LEVELS = { agents: ["kind", "agent", "model"], models: ["place", "model", "agent"], runtimes: ["model", "agent"] };
// How many of a view's first levels are counted from groups with no model.
const GROUPED = { agents: 2, models: 1, runtimes: 0 };

const norm = (v) => String(v ?? "").trim().toLowerCase();
const sum = (list) => list.reduce((n, k) => n + k.sessions, 0);

/**
 * Where a group of sessions ran. Local: its model ran on the endpoint.
 * Web chat: in the endpoint's browser. Hybrid: an agent on the endpoint
 * calling a hosted model. Remote: away from any endpoint, reaching Origin
 * through a connector.
 */
export function kindOf({ provider, source }) {
  if (norm(provider) === "local") return "local";
  const s = captureName(source);
  if (s === "browser_extension") return "web";
  return !s || ENDPOINT_SOURCES.includes(s) ? "hybrid" : "remote";
}

const PROVIDER_NAME = {
  anthropic: "Anthropic", openai: "OpenAI", google: "Google", microsoft: "Microsoft", xai: "xAI", openrouter: "OpenRouter",
  "together-ai": "Together AI", "vertex-ai": "Vertex AI", huggingface: "Hugging Face", deepseek: "DeepSeek", mistral: "Mistral",
  groq: "Groq", perplexity: "Perplexity", cursor: "Cursor", cerebras: "Cerebras", "aws-bedrock": "AWS Bedrock",
  "fireworks-ai": "Fireworks AI", "azure-openai": "Azure OpenAI", litellm: "LiteLLM", local: "Local",
};
export const providerName = (tag) => (norm(tag) ? PROVIDER_NAME[norm(tag)] || humanizeAgentName(norm(tag)) : "No provider");

/** A read's groups of sessions, each with its kind, place, agent, model and provider (for Explore). */
export function toPaths(rows = []) {
  return rows.map((r) => {
    const a = resolveAgent(r);
    const provider = norm(r.provider);
    return {
      kind: kindOf(r), place: provider === "local" ? "local" : "remote", agent: a.key, agentName: a.name,
      model: r.model ? modelKey(r.model) : "", modelName: String(r.model ?? ""), provider, sessions: Number(r.sessions) || 0,
    };
  }).filter((r) => r.sessions > 0);
}

function nameAt(r, level) {
  if (FIXED[level]) return FIXED[level][r[level]];
  return { agent: r.agentName, model: r.modelName }[level];
}

// A runtime's name; GPU inference with no runtime named is its own.
const runtimeName = (rt) => (rt.key === "local" ? "Unnamed runtime" : rt.name);

/**
 * The local runtimes seen running in the block (launches and GPU inference,
 * lib/fleetConsole.js runtimeOf), each with the endpoints it ran on and of
 * those, `quiet`, the ones with no local session in the block. `local` is the
 * block's local sessions by endpoint: [{ endpoint, sessions }].
 */
export function runtimesIn(evidence = [], local = []) {
  const served = new Set((local || []).filter((l) => Number(l.sessions) > 0).map((l) => String(l.endpoint || "")));
  const by = new Map();
  for (const e of evidence || []) {
    const rt = runtimeOf(e);
    const ep = String(e.endpoint || "");
    if (!rt || !ep) continue;
    if (!by.has(rt.key)) by.set(rt.key, { key: rt.key, name: runtimeName(rt), on: new Set() });
    by.get(rt.key).on.add(ep);
  }
  return [...by.values()]
    .map(({ on, ...r }) => ({ ...r, endpoints: on.size, quiet: [...on].filter((ep) => !served.has(ep)).length }))
    .sort((a, b) => b.endpoints - a.endpoints || a.name.localeCompare(b.name));
}

/** Runtimes installed and not seen running in the block: [{ name, installed }]. */
export function runtimesNotUsed(installed, ran = []) {
  if (!installed) return [];
  const running = new Set(ran.map((r) => r.key));
  return [...installed].filter(([key, i]) => i.installed > 0 && isRuntimeProduct(i.name) && !running.has(key)).map(([, i]) => i).sort((a, b) => b.installed - a.installed || a.name.localeCompare(b.name));
}

// The groups under a path's named steps, and the level its parts are. The
// first split (and agents', the agents) is counted from groups with no model,
// so a session that called two models counts once; below that, and local
// inference's models, from groups by model, where it counts once for each.
function under(view, data, path) {
  const named = path.filter((p) => p.id != null);
  const levels = LEVELS[view];
  const rows = named.length < GROUPED[view] ? data.groups : data.links;
  return { level: levels[named.length], rows: rows.filter((r) => named.every((p, i) => r[levels[i]] === p.id)) };
}

// Each part's sessions, its name as written most, and for a model every way
// it was written, for Explore.
export function fold(rows, level) {
  const by = new Map();
  for (const r of rows) {
    const id = r[level];
    if (!by.has(id)) by.set(id, { id, level, sessions: 0, names: new Map(), providers: new Set() });
    const n = by.get(id);
    n.sessions += r.sessions;
    n.providers.add(r.provider);
    const name = nameAt(r, level);
    n.names.set(name, (n.names.get(name) || 0) + r.sessions);
  }
  return [...by.values()].map(({ names, providers, ...n }) => {
    const written = [...names].sort((a, b) => b[1] - a[1]).map(([name]) => name);
    return { ...n, name: written[0], providers: [...providers].filter(Boolean), ...(level === "model" ? { spellings: written } : {}) };
  });
}

/**
 * The parts of the node a path ends at, the most sessions first, or a kind's
 * or place's in their own order. Past TOP, or below MIN_SHARE of the whole,
 * the rest are one part, `rest` its place in the list, that a path step
 * `{ rest }` opens. One left over is shown as itself.
 */
export function childrenOf(view, data, path = []) {
  const { level, kids } = allChildren(view, data, path);
  if (!level || FIXED[level]) return kids;
  const from = path.at(-1)?.rest ?? 0;
  const shown = kids.slice(from);
  const whole = sum(shown);
  let head = 0;
  while (head < Math.min(TOP, shown.length) && shown[head].sessions >= whole * MIN_SHARE) head++;
  // Where every part is small, the first TOP are still named.
  if (!head) head = Math.min(TOP, shown.length);
  if (shown.length - head <= 1) return shown;
  const tail = shown.slice(head);
  return [...shown.slice(0, head), { id: `rest:${from + head}`, level, rest: from + head, name: `${tail.length.toLocaleString("en-US")} ${PLURAL[level]}`, sessions: sum(tail), members: tail.map((k) => k.id), providers: [] }];
}

// Every part, unfolded: a kind's or place's in their own order, the rest the
// most sessions first.
function allChildren(view, data, path) {
  const { level, rows } = under(view, data, path);
  if (!level) return { level, kids: [] };
  const kids = fold(rows, level);
  if (FIXED[level]) return { level, kids: Object.keys(FIXED[level]).map((id) => kids.find((k) => k.id === id) || { id, level, name: FIXED[level][id], sessions: 0, providers: [] }) };
  return { level, kids: kids.sort((a, b) => b.sessions - a.sessions || a.name.localeCompare(b.name)) };
}

/**
 * The nodes a path names, step by step; null where one is gone. A named step
 * is found among all its parent's parts, folded or not, so a path can lead
 * straight to one.
 */
export function nodesOf(view, data, path = []) {
  const out = [];
  for (let i = 0; i < path.length; i++) {
    const step = path[i];
    const at = path.slice(0, i);
    const node = step.rest != null ? childrenOf(view, data, at).find((k) => k.rest === step.rest) : allChildren(view, data, at).kids.find((k) => k.id === step.id);
    if (!node) return null;
    out.push(node);
  }
  return out;
}

/** Each part's sessions in another block (the one before), by id, a rest part's its members'. */
export function sessionsIn(view, data, path, kids) {
  if (!data) return null;
  const { level, rows } = under(view, data, path);
  const by = new Map(fold(rows, level).map((k) => [k.id, k.sessions]));
  return new Map(kids.map((k) => [k.id, k.members ? k.members.reduce((n, id) => n + (by.get(id) || 0), 0) : by.get(k.id) || 0]));
}

/**
 * What Explore can search for the node a path ends at. Explore has no field
 * for an agent or for how far from the endpoint a session ran; it has the
 * provider (`agent:`) and the model. `exact` when the search is the node.
 */
export function exploreFor(nodes = []) {
  const clauses = new Set();
  let exact = true;
  for (const n of nodes) {
    if (n.rest != null) return null;
    if (n.level === "kind" || n.level === "place") {
      if (n.id === "local") clauses.add("agent:local");
      else if (n.level === "place") clauses.add("-agent:local");
      else exact = false;
    } else if (n.level === "agent") {
      if (n.providers.length === 1) clauses.add(`agent:${quote(n.providers[0])}`);
      exact = false;
    } else if (n.level === "model") clauses.add(`model:${n.spellings.map(quote).join(",")}`);
  }
  const named = nodes.some((n) => n.level === "agent" || n.level === "model");
  return named && clauses.size ? { q: [...clauses].join(" "), exact } : null;
}

// The name an agent's provider is known by, as the console's buttons say it.
export function reachedAs(provider) {
  const known = deriveAgentIdentity("", "", "", provider);
  return known.key === "unknown" ? providerName(provider) : known.name;
}

/**
 * What is installed, by agent: each product's endpoints, joined on its name as
 * the lists join it, the most installed first.
 */
export function installedBy(installed = []) {
  const by = new Map();
  for (const i of installed || []) {
    const key = installedKey(i.name);
    const n = Number(i.installed) || 0;
    if (!by.has(key) || by.get(key).installed < n) by.set(key, { name: String(i.name), provider: norm(i.provider), installed: n });
  }
  return by;
}

// An install reported with no product name.
const NAMELESS = /^\s*(|unspecified|unknown)\s*$/i;

/**
 * Products installed with nothing of theirs seen in the block: not a local
 * runtime (its sessions are local inference, which names no product), no
 * agent of its name, and no session from its provider. A product whose
 * provider was seen may be what those sessions were, so it is not listed.
 */
export function installedNotUsed(installed, paths) {
  if (!installed) return [];
  const agents = new Set(paths.map((p) => p.agent));
  const providers = new Set(paths.map((p) => p.provider));
  return [...installed.values()]
    .filter((i) => i.installed > 0 && !NAMELESS.test(i.name) && !isRuntimeProduct(i.name) && !agents.has(installedKey(i.name)) && !providers.has(i.provider))
    .sort((a, b) => b.installed - a.installed || a.name.localeCompare(b.name));
}

/**
 * Parts laid out along a length by sessions: [{ node, at, len }]. A part too
 * thin to see or reach keeps `min`, taken from the others, so its size stays
 * as near honest as it can be seen. A part with no sessions has no length,
 * and is left out unless `empty`, where it keeps its place in the order.
 */
export function split(kids, length, gap = 0, min = 3, empty = false) {
  const live = kids.filter((k) => k.sessions > 0);
  const total = sum(live);
  if (!live.length || length <= 0) return [];
  const room = Math.max(0, length - gap * (live.length - 1));
  const thin = new Set(live.filter((k) => (room * k.sessions) / total < min));
  const rest = live.filter((k) => !thin.has(k));
  const restRoom = Math.max(0, room - min * thin.size);
  const restTotal = sum(rest) || 1;
  let at = 0;
  return (empty ? kids : live).flatMap((k) => {
    if (!k.sessions) return [{ node: k, at: Math.min(at, length), len: 0 }];
    const len = thin.has(k) ? min : (restRoom * k.sessions) / restTotal;
    const part = { node: k, at, len };
    at += len + gap;
    return [part];
  });
}

/**
 * Labels above their parts ([{ node, at, len }]), `widths` the labels': each
 * { x, lane } or null. A label starts at its part, or is pulled back from the
 * end where it would run past it, and slides right past the labels already on
 * a lane; it takes the first of `lanes` lanes where it then fits. Those that fit over their parts are placed first, then a folded
 * part's, the way to the rest, then the others in order; one with no room is
 * left to its part.
 */
export function placeLabels(parts, widths, length, gap = 14, lanes = 3) {
  const out = parts.map(() => null);
  const taken = Array.from({ length: lanes }, () => []);
  const order = parts.map((_, i) => i).sort((a, b) => {
    const rank = (i) => (parts[i].len >= widths[i] ? 0 : parts[i].node?.rest != null ? 1 : 2);
    return rank(a) - rank(b) || a - b;
  });
  for (const i of order) {
    const start = Math.max(0, Math.min(parts[i].at, length - widths[i]));
    for (let lane = 0; lane < lanes; lane++) {
      let x = start;
      let hit;
      while ((hit = taken[lane].find(([a, b]) => x + widths[i] + gap > a && b + gap > x))) x = hit[1] + gap;
      if (x + widths[i] > length) continue;
      taken[lane].push([x, x + widths[i]]);
      out[i] = { x, lane };
      break;
    }
  }
  return out;
}
